import { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, User, Settings2, BellRing, Users, KeyRound, ClipboardList, ChevronDown, PanelRightClose, PanelRightOpen, Camera } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useClient } from '../../context/ClientContext';
import { useAllFindings } from '../../context/FindingsContext';
import { normalizeFinding } from '../../utils/severity';
import { isSddFinding } from '../../utils/findingClassification';
import { groupByTitle, targetLabel } from '../../utils/findingTarget';
import { useClientProfile, useActiveScan, useAssets } from '../../hooks/useClientData';
import { useHealthCheck } from '../../hooks/useHealthCheck';
import { useViewMode } from '../../context/ViewModeContext';
import { exactTime, relativeTime } from '../../utils/time';

const AVATAR_KEY = 'sentinel-command:avatar-url';

function Avatar({ avatarUrl, initials, size = 28 }) {
  return avatarUrl ? (
    <img
      src={avatarUrl}
      alt=""
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: `1px solid ${C.b1}` }}
    />
  ) : (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'linear-gradient(135deg,#334155,#1e293b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.max(10, Math.round(size * 0.34)), fontWeight: 700, color: '#fff', flexShrink: 0 }}>{initials}</div>
  );
}

function ProfileDropdown({ onClose, navigate, onLogout, user, avatarUrl, onAvatarChange }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const go = (path) => { navigate(path); onClose(); };
  const role = user?.role || 'User';
  const roleHelp = role === 'PlatformAdmin'
    ? 'Platform admins can manage MSPs, clients, integrations, and system-level settings.'
    : role.includes('Admin')
      ? 'Admins can manage client configuration, scans, findings, reports, and team access.'
      : 'This role has limited access based on assigned client and organization permissions.';
  const initials = user?.initials || (user?.name || 'User').split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();

  const handleAvatarFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => onAvatarChange(reader.result);
    reader.readAsDataURL(file);
  };

  const sections = [
    {
      items: [
        { Icon: User, label: 'My Profile', sub: 'Account details & preferences', action: () => go('/account/profile') },
        { Icon: Settings2, label: 'Account Settings', sub: 'Password, MFA & security', action: () => go('/account/settings') },
      ],
    },
    {
      title: 'Notifications',
      items: [
        { Icon: BellRing, label: 'Alert Preferences', sub: 'Email, SMS & webhook config', action: () => go('/account/alerts') },
      ],
    },
    {
      title: 'Organization',
      items: [
        { Icon: Users, label: 'Team Members', sub: 'Manage users', action: () => go('/account/team') },
        { Icon: KeyRound, label: 'API Keys', sub: 'Manage API access', action: () => go('/account/api-keys') },
        { Icon: ClipboardList, label: 'Audit Log', sub: 'View all activity', action: () => go('/account/audit-log') },
      ],
    },
  ];

  return (
    <div ref={ref} className="topbar-profile-dropdown" style={{
      position: 'absolute', top: '100%', right: 0, marginTop: 6,
      width: 280, background: C.s1, border: `1px solid ${C.b1}`,
      borderRadius: 8, boxShadow: '0 18px 44px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)',
      zIndex: 1000, overflow: 'hidden',
    }}>
      {/* User header */}
      <div style={{ padding: '16px 16px 14px', borderBottom: `1px solid ${C.b1}`, background: C.s2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            <Avatar avatarUrl={avatarUrl} initials={initials} size={38} />
            <button
              type="button"
              title="Upload profile image"
              onClick={() => fileRef.current?.click()}
              style={{ position: 'absolute', right: -4, bottom: -4, width: 18, height: 18, borderRadius: '50%', border: `1px solid ${C.b1}`, background: C.s1, color: C.t2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
            >
              <Camera size={11} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatarFile} style={{ display: 'none' }} />
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.t1, fontFamily: FONTS.sans }}>{user?.name || 'User'}</div>
            <div style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{user?.email || ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <span title={roleHelp} style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: `${C.sky}15`, color: C.sky, border: `1px solid ${C.sky}33`, fontFamily: FONTS.mono }}>{role}</span>
          <span style={{ fontSize: 9, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: `${C.grn}15`, color: C.grn, border: `1px solid ${C.grn}33`, fontFamily: FONTS.mono }}>MFA Enabled</span>
        </div>
      </div>

      {/* Menu sections */}
      {sections.map((sec, si) => (
        <div key={si} style={{ borderBottom: si < sections.length - 1 ? `1px solid ${C.b1}` : 'none' }}>
          {sec.title && <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: 0, padding: '10px 16px 4px' }}>{sec.title}</div>}
          {sec.items.map((item, ii) => (
            <div key={ii} onClick={item.action} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px',
              cursor: 'pointer', transition: 'background 0.1s',
            }} onMouseEnter={e => e.currentTarget.style.background = C.s2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ display: 'inline-flex', justifyContent: 'center', width: 16, color: C.t2, opacity: 0.85 }}><item.Icon size={14} strokeWidth={1.75} /></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: C.t1, fontFamily: FONTS.sans }}>{item.label}</div>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono, marginTop: 1 }}>{item.sub}</div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Session info + sign out */}
      <div style={{ borderTop: `1px solid ${C.b1}`, padding: '10px 16px' }}>
        <div onClick={() => { onLogout(); go('/login'); }} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '7px 0', borderRadius: 6, cursor: 'pointer',
          background: C.redBg, border: `1px solid ${C.red}33`,
          fontSize: 11, fontWeight: 600, color: C.red, fontFamily: FONTS.sans,
          transition: 'opacity 0.1s',
        }} onMouseEnter={e => e.currentTarget.style.opacity = '0.8'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
          Sign Out
        </div>
      </div>
    </div>
  );
}

function GlobalSearch({ navigate, onRequestOpen }) {
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);
  const { findings: rawFindings } = useAllFindings();
  const { data: assets } = useAssets();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        onRequestOpen(true);
        setOpen(true);
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        });
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onRequestOpen]);

  useEffect(() => {
    setQuery('');
    setOpen(false);
    const clearAutofill = () => {
      if (inputRef.current) inputRef.current.value = '';
    };
    clearAutofill();
    const timer = setTimeout(clearAutofill, 250);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const results = useMemo(() => {
    if (!query || query.length < 2) return { findings: [], assets: [], cveAction: null };
    const trimmedQuery = query.trim();
    const q = trimmedQuery.toLowerCase();
    const cveAction = location.pathname.startsWith('/client/') ? cveSearchAction(trimmedQuery) : null;
    const allFindings = rawFindings.map(normalizeFinding).filter(Boolean).filter(f => !isSddFinding(f));
    const matchedFindings = groupByTitle(allFindings
      .filter(f => findingSearchText(f).includes(q)))
      .slice(0, 5);
    const assetList = Array.isArray(assets) ? assets : [];
    const matchedAssets = assetList
      .filter(a => (a.hostname || '').toLowerCase().includes(q) || (a.ipAddress || '').toLowerCase().includes(q) || (a.displayName || '').toLowerCase().includes(q))
      .slice(0, 5);
    return { findings: matchedFindings, assets: matchedAssets, cveAction };
  }, [query, rawFindings, assets, location.pathname]);

  const hasResults = results.findings.length > 0 || results.assets.length > 0 || Boolean(results.cveAction);
  const hasSearchQuery = query.length >= 2;
  const openSearchResults = (searchQuery = query) => {
    navigate(`/client/findings?search=${encodeURIComponent(searchQuery)}`);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: 'min(30vw, 360px)', minWidth: 220 }}>
      <input
        ref={inputRef}
        type="search"
        name="sentinel-global-search-query"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search findings, assets..."
        title="Global search. Press Cmd-K or Ctrl-K from anywhere."
        style={{
          width: '100%', height: 34, background: C.bg, border: `1px solid ${C.b1}`, borderRadius: 8,
          padding: '7px 10px 7px 30px', color: C.t1, fontSize: 11, fontFamily: FONTS.mono,
          outline: 'none',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.02)',
        }}
      />
      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', color: C.t3 }}><Search size={13} strokeWidth={2} /></span>

      {open && hasSearchQuery && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 8,
          boxShadow: '0 18px 42px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.03)', zIndex: 1000, maxHeight: 360, overflowY: 'auto',
        }}>
          {!hasResults && (
            <div style={{ padding: '14px 12px', fontSize: 11, color: C.t3, fontFamily: FONTS.mono, textAlign: 'center' }}>No quick matches for "{query}"</div>
          )}
          {results.findings.length > 0 && (
            <>
              <div style={{ padding: '8px 12px 4px', fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: 0 }}>Findings</div>
              {results.findings.map(g => {
                const f = g.first;
                return (
                <div key={g.key} onClick={() => { navigate(`/client/findings/${f.id}`); setOpen(false); setQuery(''); }}
                  style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.b2}` }}
                  onMouseEnter={e => e.currentTarget.style.background = C.s2}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontSize: 11, color: C.t1, fontWeight: 500 }}>{f.title}{g.count > 1 ? ` (${g.count})` : ''}</div>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>{f.sev} / {targetLabel(f, g.count) || f.asset || '-'}</div>
                </div>
                );
              })}
            </>
          )}
          {results.assets.length > 0 && (
            <>
              <div style={{ padding: '8px 12px 4px', fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: 0 }}>Assets</div>
              {results.assets.map(a => (
                <div key={a.assetId} onClick={() => { navigate(`/client/assets/${a.assetId}`); setOpen(false); setQuery(''); }}
                  style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.b2}` }}
                  onMouseEnter={e => e.currentTarget.style.background = C.s2}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontSize: 11, color: C.t1, fontWeight: 500 }}>{a.displayName || a.hostname || a.ipAddress}</div>
                  <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>{[a.ipAddress, a.hostname].filter(Boolean).join(' / ')}</div>
                </div>
              ))}
            </>
          )}
          {results.cveAction && (
            <>
              <div style={{ padding: '8px 12px 4px', fontSize: 9, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: 0 }}>CVE Findings</div>
              <div onClick={() => openSearchResults(results.cveAction.search)}
                style={{ padding: '7px 12px', cursor: 'pointer', borderBottom: `1px solid ${C.b2}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.s2}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div style={{ fontSize: 11, color: C.t1, fontWeight: 500 }}>{results.cveAction.label}</div>
                <div style={{ fontSize: 9, color: C.t2, fontFamily: FONTS.mono, marginTop: 1 }}>Open CVE findings filtered to this search</div>
              </div>
            </>
          )}
          {hasSearchQuery && (
            <div onClick={() => openSearchResults()}
              style={{ padding: '8px 12px', fontSize: 10, color: C.sky, fontFamily: FONTS.mono, cursor: 'pointer', textAlign: 'center', borderTop: `1px solid ${C.b1}` }}
              onMouseEnter={e => e.currentTarget.style.background = C.s2}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              Search all findings for "{query}" →
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function cveSearchAction(query) {
  const exact = query.match(/\bCVE-\d{4}-\d{4,7}\b/i)?.[0]?.toUpperCase();
  if (exact) return { search: exact, label: `View ${exact} in CVE findings` };
  if (/^cve\b/i.test(query)) return { search: query, label: `Search CVE findings for "${query}"` };
  return null;
}

function findingSearchText(f) {
  const detailValues = f.detail && typeof f.detail === 'object'
    ? Object.values(f.detail).flatMap(value => Array.isArray(value) ? value : [value])
    : [];
  const referenceValues = Array.isArray(f.references) ? f.references : [];
  const complianceValues = Array.isArray(f.complianceMappings) ? f.complianceMappings : [];
  return [
    f.title, f.id, f.asset, f.hostname, f.ipAddress, f.cve, f.cveId, f.module, f.source, f.fingerprint,
    f.description, f.summary, f.evidence, f.recommendation, f.remediation,
    ...detailValues,
    ...referenceValues,
    ...complianceValues,
  ].filter(Boolean).join(' ').toLowerCase();
}

export default function Topbar({ title, sub, extraPill, showAnalysisToggle = false, analysisRailOpen = false, analysisHasContent = false, onToggleAnalysisRail }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { resetTheme } = useTheme();
  const isMSP = location.pathname.startsWith('/msp');
  const [profileOpen, setProfileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);
  useEffect(() => {
    const close = event => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'keydown' || !toolsRef.current?.contains(event.target)) setToolsOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, []);
  useEffect(() => { setToolsOpen(false); setProfileOpen(false); }, [location.pathname]);
  const [avatarUrl, setAvatarUrl] = useState(() => {
    try { return localStorage.getItem(AVATAR_KEY) || ''; } catch { return ''; }
  });
  const { mode, setMode } = useViewMode();
  const displayName = user?.name || 'User';
  const initials = user?.initials || displayName.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const shortName = displayName.includes(' ') ? `${displayName.split(' ')[0][0]}. ${displayName.split(' ').slice(-1)[0]}` : displayName;

  const { clientName: ctxName } = useClient();
  const { data: profile } = useClientProfile();
  const { isRunning } = useActiveScan();
  const { status: healthStatus, lastCheck } = useHealthCheck();
  const clientName = ctxName || profile?.name || profile?.companyName || null;
  const healthColor = healthStatus === 'healthy' ? C.grn : healthStatus === 'unhealthy' ? C.red : C.t3;
  const connectionTitle = `Sentinel Cloud API / ${healthStatus === 'healthy' ? 'Connected' : healthStatus === 'unhealthy' ? 'Offline' : 'Checking'}${lastCheck ? ` / Last sync ${relativeTime(lastCheck)} (${exactTime(lastCheck)})` : ''}`;

  const handleAvatarChange = (value) => {
    const next = typeof value === 'string' ? value : '';
    setAvatarUrl(next);
    try {
      if (next) localStorage.setItem(AVATAR_KEY, next); else localStorage.removeItem(AVATAR_KEY);
    } catch { /* ignore */ }
  };

  return (
    <header className="topbar" style={{
      minHeight: 64, position: 'relative', background: 'var(--color-surface, ' + C.s1 + ')',
      borderBottom: `1px solid ${C.b1}`, display: 'flex', alignItems: 'center',
      padding: '10px 24px', gap: 12, flexShrink: 0, flexWrap: 'wrap',
      boxShadow: '0 1px 0 rgba(255,255,255,0.02)',
    }}>
      <style>{`
        .topbar-title { flex: 1 1 200px; min-width: 0; overflow-wrap: anywhere; }
        .topbar-tools, .topbar-tools-panel { display: contents; }
        .topbar-tools-toggle { display: none; }
        @media (max-width: 1200px) {
          .topbar-tools { display: block; flex-shrink: 0; }
          .topbar-tools-toggle { display: inline-flex; }
          .topbar-tools-panel { display: none; }
          .topbar-tools-panel.is-open {
            display: flex; flex-wrap: wrap; align-items: center; gap: 12px;
            position: absolute; top: 100%; right: 12px; z-index: 1100;
            width: min(360px, calc(100vw - 24px)); padding: 12px;
            background: ${C.s1}; border: 1px solid ${C.b1}; border-radius: 8px;
            box-shadow: 0 18px 44px rgba(0,0,0,0.28);
          }
          .topbar-tools-panel > * { max-width: 100%; overflow-wrap: anywhere; }
          .topbar-tools-panel > div:first-child { width: 100% !important; min-width: 0 !important; }
        }
        @media (max-width: 768px) {
          .topbar { padding: 10px 12px 10px 60px !important; }
          .topbar-account-menu { position: static !important; }
          .topbar-profile-dropdown { right: 12px !important; max-width: calc(100vw - 24px); }
        }
        @media (max-width: 480px) {
          .topbar-title { flex-basis: 100%; }
          .topbar-tools { margin-left: auto; }
          .topbar-account-details { display: none; }
        }
      `}</style>
      <div className="topbar-title">
        <div style={{ fontSize: 18, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, letterSpacing: 0, lineHeight: 1.2 }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginTop: 2 }}>{sub}</div>}
      </div>

      <div className="topbar-tools" ref={toolsRef}>
        <button type="button" className="topbar-tools-toggle" aria-label="Header tools"
          aria-expanded={toolsOpen} aria-controls="topbar-tools-panel"
          onClick={() => setToolsOpen(open => !open)}
          style={{ alignItems: 'center', justifyContent: 'center', gap: 4, height: 36, padding: '0 8px',
            borderRadius: 8, border: `1px solid ${C.b1}`, background: C.s2, color: C.t1, cursor: 'pointer' }}>
          <Search size={16} /><ChevronDown size={12} />
        </button>
        <div id="topbar-tools-panel" className={`topbar-tools-panel${toolsOpen ? ' is-open' : ''}`}>
      {/* Global search */}
      <GlobalSearch navigate={navigate} onRequestOpen={setToolsOpen} />

      <div title={`Current mode: ${mode === 'executive' ? 'Executive view shows board-ready impact, talk tracks, and compliance context.' : 'Technical view shows scanner evidence, CVE/EPSS, affected targets, and validation detail.'}`} style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 34,
        borderRadius: 8,
        border: `1px solid ${C.b1}`,
        background: C.s2,
        padding: 3,
        gap: 2,
        flexShrink: 0,
      }}>
        {['executive', 'technical'].map(option => {
          const active = mode === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => setMode(option)}
              aria-pressed={active}
              title={option === 'executive'
                ? 'Executive view: client-ready business impact, talk tracks, and compliance context'
                : 'Technical view: CVE/EPSS, scanner evidence, affected assets, and validation detail'}
              style={{
                height: 26,
                padding: '0 12px',
                borderRadius: 6,
                border: 'none',
                background: active ? C.s1 : 'transparent',
                color: active ? C.t1 : C.t2,
                boxShadow: active ? '0 6px 16px rgba(0,0,0,0.16)' : 'none',
                fontSize: 10,
                fontWeight: 800,
                fontFamily: FONTS.sans,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {option === 'executive' ? 'Executive' : 'Technical'}
            </button>
          );
        })}
      </div>

      {showAnalysisToggle && (
        <button
          type="button"
          title={analysisRailOpen ? 'Close analyst analysis' : 'Open analyst analysis'}
          aria-label={analysisRailOpen ? 'Close analyst analysis' : 'Open analyst analysis'}
          aria-pressed={analysisRailOpen}
          onClick={onToggleAnalysisRail}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            height: 34,
            padding: '0 11px',
            borderRadius: 8,
            border: `1px solid ${analysisRailOpen ? C.sky : analysisHasContent ? `${C.sky}88` : C.b1}`,
            background: analysisRailOpen ? `${C.sky}14` : analysisHasContent ? `${C.sky}0d` : C.s2,
            color: analysisRailOpen || analysisHasContent ? C.sky : C.t2,
            fontSize: 10,
            fontWeight: 700,
            fontFamily: FONTS.mono,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            boxShadow: analysisRailOpen
              ? `0 0 0 1px ${C.sky}18, 0 10px 24px ${C.sky}10`
              : analysisHasContent
                ? `0 0 0 1px ${C.sky}12, 0 0 14px ${C.sky}22`
                : 'none',
          }}
        >
          {analysisRailOpen ? <PanelRightClose size={14} /> : <PanelRightOpen size={14} />}
          <span>Analysis</span>
        </button>
      )}

      {/* Scan status indicator */}
      {!isMSP && isRunning && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: `${C.grn}10`, border: `1px solid ${C.grn}33` }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.grn, boxShadow: `0 0 6px ${C.grn}`, animation: 'pulse 1.2s ease-in-out infinite' }} />
          <span style={{ fontSize: 10, fontWeight: 600, color: C.grn, fontFamily: FONTS.mono }}>Scan Running</span>
        </div>
      )}

      {/* Extra pill (e.g. Platform Admin badge) */}
      {extraPill}

      {/* Client context pill */}
      {!isMSP && clientName && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: C.s2, border: `1px solid ${C.b1}` }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.grn }} />
          <span style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono }}>{clientName}</span>
        </div>
      )}

      {/* Connection status dot */}
      <div title={connectionTitle} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 9px', borderRadius: 8, background: `${healthColor}10`, border: `1px solid ${healthColor}33` }}>
        <div style={{ width: 6, height: 6, borderRadius: '50%', background: healthColor, boxShadow: healthStatus === 'healthy' ? `0 0 4px ${healthColor}` : 'none' }} />
        <span style={{ fontSize: 9, color: healthColor, fontFamily: FONTS.mono }}>{healthStatus === 'healthy' ? 'Connected' : healthStatus === 'unhealthy' ? 'Offline' : '...'}</span>
      </div>

        </div>
      </div>
      {/* User profile */}
      <div className="topbar-account-menu" style={{ position: 'relative', flexShrink: 0, maxWidth: '100%' }}>
        <button type="button" aria-label="Account menu" aria-expanded={profileOpen}
          onClick={() => { setProfileOpen(p => !p); setToolsOpen(false); }} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', borderRadius: 8,
          background: profileOpen ? C.s2 : 'transparent', border: `1px solid ${profileOpen ? C.b1 : 'transparent'}`,
          cursor: 'pointer', transition: 'all 0.12s',
        }}>
          <Avatar avatarUrl={avatarUrl} initials={initials} size={28} />
          <div className="topbar-account-details" style={{ maxWidth: 140, overflowWrap: 'anywhere', textAlign: 'left' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, fontFamily: FONTS.sans, lineHeight: 1.2 }}>{shortName}</div>
            <div title="Hover the role chip in the profile menu to see permission scope." style={{ fontSize: 9, color: C.t3, fontFamily: FONTS.mono }}>{user?.role || 'User'}</div>
          </div>
          <ChevronDown size={12} color={C.t3} style={{ marginLeft: 2 }} />
        </button>
        {profileOpen && <ProfileDropdown onClose={() => setProfileOpen(false)} navigate={navigate} onLogout={() => { logout(); resetTheme(); }} user={user} avatarUrl={avatarUrl} onAvatarChange={handleAvatarChange} />}
      </div>
    </header>
  );
}
